// 필요한 모듈 추가
import React, { useEffect, useMemo, useState } from "react";
import { MakingRequest, Status } from "../types/type";
import {
  deleteMakingRequest,
  fetchMakingRequestList,
  updateMakingRequest,
  updateSongRequestToFail,
} from "../api/pages/makingRequest.api";
import "./styles/MakingRequestPage.css";
import { addNewSong } from "../api/write.api";
import { useRecoilValue } from "recoil";
import { artistListState } from "../atom/atom";
import { useThumbnail } from "../hooks/useThumbnail";
import { usePushNotificationForm } from "../hooks/usePushNotificationForm";
import { useToast } from "../hooks/useToast";
import { generateSongId } from "../api/fetch.api";
import { updateSongRequestToComplete } from "../api/update.api";
import { storage } from "../firebase/firebaseConfig";
import { ref, uploadBytes } from "firebase/storage";
import { normalizeAudioFile } from "../tools/audio.tool";
import { localStorageUtil } from "../module/util";
import AudioDropzone from "../components/file/AudioDropzone";

const MakingRequestPage = () => {
  const totalArtistList = useRecoilValue(artistListState);

  const [makingRequestList, setMakingRequestList] = useState<MakingRequest[]>(
    []
  );

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<
    "songTitle" | "artistName" | "existingArtist" | null
  >(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [statusFilter, setStautsFilter] = useState<Status>(Status.PENDING);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalArtistName, setModalArtistName] = useState<string | null>(null);
  const [nextSongId, setNextSongId] = useState<string>("");

  const [progressMessage, setProgressMessage] = useState<string>("대기");
  const [thumbnailProgressMessages, setThumbnailProgressMessages] = useState<
    Record<string, string>
  >({});
  // 오디오 관련
  const [progressMessages, setProgressMessages] = useState<
    Record<string, string>
  >({});
  const [audioFiles, setAudioFiles] = useState<Record<string, File | null>>({});
  const [normalizedAudioFiles, setNormalizedAudioFiles] = useState<
    Record<string, File | null>
  >({});
  const [isAudioNormalized, setIsAudioNormalized] = useState<
    Record<string, boolean>
  >({});
  const [thumbnailFiles, setThumbnailFiles] = useState<
    Record<string, File | null>
  >({}); // <-- Now it's a map

  const [pendingThumbnailUpload, setPendingThumbnailUpload] = useState<
    string | null
  >(null); // 업로드를 기다리는 요청 ID

  // 썸네일 파일 업로드를 추적하는 useEffect
  useEffect(() => {
    const uploadPendingThumbnail = async () => {
      if (pendingThumbnailUpload && thumbnailFiles[pendingThumbnailUpload]) {
        await handleThumbnailUpload(
          pendingThumbnailUpload,
          makingRequestList.find(
            (request) => request.songRequestId === pendingThumbnailUpload
          )?.existingArtist?.artistName || ""
        );
        setPendingThumbnailUpload(null); // 업로드 후 상태 초기화
      }
    };

    uploadPendingThumbnail();
  }, [pendingThumbnailUpload, thumbnailFiles, makingRequestList]);

  const {
    totalArtistThumbnailList,
    fetchAllArtistThumbnailList,
    removeCachedArtistThumbnailList,
    uploadArtistThumbnail,
  } = useThumbnail();

  const { handleSingleSendNotification } = usePushNotificationForm();
  const { fireToast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      const data = await fetchMakingRequestList();

      setMakingRequestList(data);
    };
    fetchData();
  }, []);

  const calculateNextSongId = async () => {
    const newId = await generateSongId();
    setNextSongId(newId);
  };

  useEffect(() => {
    if (totalArtistList.length > 0) {
      fetchAllArtistThumbnailList();
      calculateNextSongId();
    }
  }, [totalArtistList.length]);

  const handleDoubleClick = (
    id: string,
    field: "songTitle" | "artistName" | "existingArtist",
    value: string
  ) => {
    setEditingId(id);
    setEditingField(field);
    if (field === "songTitle" || field === "artistName") {
      setInputValue(value);
    }
  };
  const handleThumbnailUpload = async (
    requestId: string,
    artistName: string
  ) => {
    const file = thumbnailFiles[requestId]; // Get the specific thumbnail file
    if (!file) {
      window.alert("썸네일 이미지를 선택해야 합니다.");
      return;
    }

    try {
      setThumbnailProgressMessages((prev) => ({
        ...prev,
        [requestId]: "썸네일 업로드 중...",
      }));

      const thumbnailUrl = await uploadArtistThumbnail(artistName, file);

      setThumbnailProgressMessages((prev) => ({
        ...prev,
        [requestId]: "썸네일 업로드 완료!",
      }));

      const updatedRequest = makingRequestList.find(
        (request) => request.songRequestId === requestId
      );
      if (updatedRequest) {
        updatedRequest.thumbnailUrl = thumbnailUrl; // Automatically assign thumbnailUrl
        await updateMakingRequest(requestId, { thumbnailUrl });
        setMakingRequestList([...makingRequestList]);
      }
    } catch (error) {
      setThumbnailProgressMessages((prev) => ({
        ...prev,
        [requestId]: "썸네일 업로드 실패",
      }));
      console.error("Error uploading thumbnail: ", error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleAudioChange = async (
    e: React.ChangeEvent<HTMLInputElement> | { target: { files: FileList } },
    requestId: string
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setAudioFiles((prev) => ({ ...prev, [requestId]: file }));
      setProgressMessages((prev) => ({
        ...prev,
        [requestId]: "오디오 정규화 중...",
      }));
      setIsAudioNormalized((prev) => ({ ...prev, [requestId]: false }));

      // Automatically start normalization
      const normalizedFile = await normalizeAudioFile(file);
      setNormalizedAudioFiles((prev) => ({
        ...prev,
        [requestId]: normalizedFile,
      }));
      setIsAudioNormalized((prev) => ({ ...prev, [requestId]: true }));
      setProgressMessages((prev) => ({
        ...prev,
        [requestId]: "오디오 정규화 완료!",
      }));
    }
  };
  const handleKeyPress = async (
    e: React.KeyboardEvent<HTMLInputElement>,
    id: string
  ) => {
    if (e.key === "Enter" && editingField) {
      const updatedRequest = makingRequestList.find(
        (request) => request.songRequestId === id
      );
      if (updatedRequest) {
        if (editingField === "songTitle" || editingField === "artistName") {
          updatedRequest[editingField] = inputValue;
        }
        await updateMakingRequest(id, { [editingField]: inputValue });
        setMakingRequestList([...makingRequestList]);
        setEditingId(null);
        setEditingField(null);
        setInputValue("");
      }
    }
  };

  const handleArtistSelectChange = async (
    e: React.ChangeEvent<HTMLSelectElement>,
    id: string
  ) => {
    const selectedArtist = totalArtistList.find(
      (artist) => artist.artistId === e.target.value
    );
    if (selectedArtist) {
      const updatedRequest = makingRequestList.find(
        (request) => request.songRequestId === id
      );
      if (updatedRequest) {
        updatedRequest.existingArtist = selectedArtist;
        await updateMakingRequest(id, { existingArtist: selectedArtist });
        setMakingRequestList([...makingRequestList]);
        setEditingId(null);
        setEditingField(null);
      }
    }
  };

  const handleThumbnailChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    requestId: string
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setThumbnailFiles((prev) => ({ ...prev, [requestId]: file }));
      setPendingThumbnailUpload(requestId); // 업로드 대기 설정
      return file;
    }
  };

  const handleComplete = async (makingRequest: MakingRequest) => {
    // 곡 추가
    //// 해당 아티스트, 해당 제목, makerUserId 해당되는 걸로.
    //// makerUserId 여러개일 경우 콤마 상태로 string으로 하드코딩.
    if (!makingRequest.existingArtist)
      return window.alert("아티스트를 선택해야함.");

    const requestId = makingRequest.songRequestId;
    if (!audioFiles[requestId])
      return window.alert("오디오 파일을 선택해야 합니다.");
    if (!isAudioNormalized[requestId] || !normalizedAudioFiles[requestId])
      return window.alert("오디오 정규화가 완료되지 않았습니다.");

    try {
      setProgressMessage("정규화 오디오 파일 업로드 중...");
      const audioRef = ref(storage, `${nextSongId}.mp3`);
      await uploadBytes(audioRef, normalizedAudioFiles[requestId] as File);

      setProgressMessage("db 업데이트 중...");
      const newSongId = await addNewSong({
        artistId: makingRequest.existingArtist.artistId,
        artistName: makingRequest.existingArtist.artistName,
        title: makingRequest.songTitle,
        thumbnailUrl: makingRequest.thumbnailUrl,
        youtubeId: "",
        tagList: [],
        isAI: true,
        makerUserId: makingRequest.userName,
        tag: { genreList: [], tagList: [] },
      });

      if (!newSongId) return window.alert("곡 제작 에러");

      // 신청곡 상태 변경
      //// makingRequest status 변경
      await updateMakingRequest(makingRequest.songRequestId, {
        status: Status.COMPLETE,
      });

      //// songRequest status 변경
      ////// songRequest 컬렉션
      ////// user 컬렉션
      await updateSongRequestToComplete({
        songRequestId: makingRequest.songRequestId,
        userId: makingRequest.userId,
        newSongId: newSongId,
      });

      // 알림 전송
      await handleSingleSendNotification({
        token: makingRequest.userToken,
        screen: "MyBrewingList",
        songId: newSongId,
        type: "제조완료",
        title: "제조 완료",
        content: "내 곡이 만들어졌어요. 지금 바로 들어보세요!",
      });

      setAudioFiles((prev) => ({ ...prev, [requestId]: null }));
      setNormalizedAudioFiles((prev) => ({ ...prev, [requestId]: null }));
      setIsAudioNormalized((prev) => ({ ...prev, [requestId]: false }));
      fireToast({ content: `곡 제작 완료 : ${newSongId}` });
      setProgressMessages((prev) => ({ ...prev, [requestId]: "대기" }));
      calculateNextSongId();
      setProgressMessage("완료");
    } catch (error: any) {
      setProgressMessage("대기");
      window.alert("handleComplete 에러 : " + error.message);
    }
  };

  const handleCompleteNoMaker = async (
    makingRequest: MakingRequest,
    newSongId: string
  ) => {
    if (!makingRequest.existingArtist)
      return window.alert("아티스트를 선택해야함.");

    const requestId = makingRequest.songRequestId;

    try {
      // 신청곡 상태 변경
      //// makingRequest status 변경
      await updateMakingRequest(makingRequest.songRequestId, {
        status: Status.COMPLETE,
      });

      //// songRequest status 변경
      ////// songRequest 컬렉션
      ////// user 컬렉션
      await updateSongRequestToComplete({
        songRequestId: makingRequest.songRequestId,
        userId: makingRequest.userId,
        newSongId: newSongId,
      });

      // 알림 전송
      await handleSingleSendNotification({
        token: makingRequest.userToken,
        screen: "MyBrewingList",
        songId: newSongId,
        type: "제조완료",
        title: "제조 완료",
        content: "내 곡이 만들어졌어요.",
      });

      fireToast({ content: `알림 전송 완료 : ${newSongId}` });
      setProgressMessages((prev) => ({ ...prev, [requestId]: "대기" }));
      calculateNextSongId();
      setProgressMessage("완료");
    } catch (error: any) {
      setProgressMessage("대기");
      window.alert("handleComplete 에러 : " + error.message);
    }
  };

  const handleFail = async (makingRequest: MakingRequest) => {
    try {
      // 신청곡 상태를 실패로 변경
      await updateMakingRequest(makingRequest.songRequestId, {
        status: Status.FAILED,
      });

      // songRequest 상태도 실패로 변경
      await updateSongRequestToFail({
        songRequestId: makingRequest.songRequestId,
        userId: makingRequest.userId,
      });

      // 실패 알림 전송
      await handleSingleSendNotification({
        token: makingRequest.userToken,
        screen: "MyBrewingList",
        songId: "-1", // 실패 시 songId는 '-1'
        type: "제조실패",
        title: "제조 실패",
        content:
          "곡 제작에 실패했어요... 다른 곡을 신청해보세요! 가이드에 따르면 제조 확률을 높일 수 있답니다!",
      });

      fireToast({ content: `곡 제작 실패: ${makingRequest.songTitle}` });
    } catch (error: any) {
      window.alert("handleFail 에러: " + error.message);
    }
  };

  const handleDelete = async (makingRequest: MakingRequest) => {
    await deleteMakingRequest(makingRequest);
    setMakingRequestList((prev) => [
      ...prev.filter((r) => r.songRequestId !== makingRequest.songRequestId),
    ]);
  };

  const handleThumbnailClick = (makingRequest: MakingRequest) => {
    setEditingId(makingRequest.songRequestId);
    if (!makingRequest.existingArtist)
      return window.alert("아티스트 선택 먼저");
    setModalArtistName(makingRequest.existingArtist.artistName);
    setIsModalOpen(true);
  };

  const handleThumbnailSelect = async (thumbnailUrl: string, id: string) => {
    const updatedRequest = makingRequestList.find(
      (request) => request.songRequestId === id
    );
    if (updatedRequest) {
      updatedRequest.thumbnailUrl = thumbnailUrl;
      await updateMakingRequest(id, { thumbnailUrl });
      setMakingRequestList([...makingRequestList]);
      setIsModalOpen(false);
    }
  };

  const handleCopyToClipboard = () => {
    const artistGroupedSongs = makingRequestList
      .filter((request) => request.status === Status.PENDING)
      .reduce((acc, request) => {
        const artistName =
          request.existingArtist?.artistName || request.artistName;
        if (!acc[artistName]) {
          acc[artistName] = [];
        }
        acc[artistName].push(request.songTitle);
        return acc;
      }, {} as Record<string, string[]>);

    const clipboardText = Object.entries(artistGroupedSongs)
      .map(
        ([artistName, songs]) =>
          `[${artistName}]\n${songs.map((song) => `${song}`).join("\n")}`
      )
      .join("\n\n");

    navigator.clipboard.writeText(clipboardText).then(() => {
      fireToast({ content: "복사 완료!" });
    });
  };

  const filteredMakingRequestList = useMemo(() => {
    return makingRequestList.filter(
      (makingRequest) => makingRequest.status === statusFilter
    );
  }, [statusFilter, makingRequestList]);

  return (
    <div>
      <div className="header">
        <div className="header-col1">
          <h1>Making Requests</h1>
          <h3>
            <a
              target="썸네일 스토리지 링크"
              href="https://console.firebase.google.com/project/homebrew-prod/storage/homebrew-prod.appspot.com/files/~2FartistThumbnail?hl=ko"
            >
              썸네일 스토리지 링크
            </a>
          </h3>
          {progressMessage && (
            <div className="progress-message">
              진행 상태 : {nextSongId} {progressMessage}
            </div>
          )}
        </div>

        <div className="header-col2">
          <div className="header-col2">
            <button onClick={() => setStautsFilter(Status.PENDING)}>
              pending
            </button>
            <button onClick={() => setStautsFilter(Status.COMPLETE)}>
              complete
            </button>
          </div>
          <div>
            <button onClick={handleCopyToClipboard}>복사하기</button>
          </div>
          <div>
            <button
              onClick={() => {
                // removeCachedArtistThumbnailList();
                localStorageUtil.clear();
                window.location.reload();
              }}
            >
              캐시 삭제
            </button>
          </div>
        </div>
      </div>
      <table className="making-request-table">
        <thead>
          <tr>
            <th style={{ backgroundColor: "orange" }}>제목</th>
            <th style={{ backgroundColor: "orange" }}>오디오 파일</th>
            <th style={{ backgroundColor: "orange" }}>아티스트</th>
            <th>신청 아티스트</th>
            <th style={{ backgroundColor: "orange" }}>썸네일</th>
            <th>MakerUserId</th>
            <th>Status</th>
            <th colSpan={4}>비고</th>
          </tr>
        </thead>
        <tbody>
          {filteredMakingRequestList.map((request) => (
            <tr key={request.songRequestId}>
              <td
                onDoubleClick={() =>
                  handleDoubleClick(
                    request.songRequestId,
                    "songTitle",
                    request.songTitle
                  )
                }
              >
                {editingId === request.songRequestId &&
                editingField === "songTitle" ? (
                  <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyPress={(e) => handleKeyPress(e, request.songRequestId)}
                    onBlur={() => {
                      setEditingId(null);
                      setEditingField(null);
                    }}
                  />
                ) : (
                  request.songTitle
                )}
              </td>
              <td>
                <AudioDropzone
                  onFileChange={handleAudioChange}
                  requestId={request.songRequestId}
                  progressMessage={progressMessages[request.songRequestId]}
                />
                {/* <input
                  type="file"
                  onChange={(e) => handleAudioChange(e, request.songRequestId)}
                /> */}
                {/* {normalizedAudioFiles[request.songRequestId] && (
                  <audio
                    controls
                    src={URL.createObjectURL(
                      normalizedAudioFiles[request.songRequestId] as File
                    )}
                  />
                )} */}

                <div>{progressMessages[request.songRequestId]}</div>
              </td>
              <td
                onDoubleClick={() =>
                  handleDoubleClick(
                    request.songRequestId,
                    "existingArtist",
                    request.existingArtist?.artistName || ""
                  )
                }
              >
                {editingId === request.songRequestId &&
                editingField === "existingArtist" ? (
                  <select
                    value={request.existingArtist?.artistId || ""}
                    onChange={(e) =>
                      handleArtistSelectChange(e, request.songRequestId)
                    }
                    onBlur={() => setEditingId(null)}
                  >
                    {totalArtistList.map((artist) => (
                      <option key={artist.artistId} value={artist.artistId}>
                        {artist.artistName}
                      </option>
                    ))}
                  </select>
                ) : (
                  request.existingArtist?.artistName
                )}
              </td>
              <td
                onDoubleClick={() =>
                  handleDoubleClick(
                    request.songRequestId,
                    "artistName",
                    request.artistName
                  )
                }
              >
                {editingId === request.songRequestId &&
                editingField === "artistName" ? (
                  <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyPress={(e) => handleKeyPress(e, request.songRequestId)}
                    onBlur={() => {
                      setEditingId(null);
                      setEditingField(null);
                    }}
                  />
                ) : (
                  request.artistName
                )}
              </td>
              <td>
                {request.thumbnailUrl ? (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <img
                      src={request.thumbnailUrl}
                      alt="썸네일"
                      style={{ width: 192 / 2.5, height: 100 / 2.5 }}
                      onClick={(e) => {
                        e.stopPropagation(); // 이벤트 전파 방지
                        handleThumbnailClick(request);
                      }}
                    />
                    <button
                      onClick={async () => {
                        await updateMakingRequest(request.songRequestId, {
                          thumbnailUrl: "",
                        });
                        setMakingRequestList((prev) =>
                          prev.map((r) =>
                            r.songRequestId === request.songRequestId
                              ? { ...r, thumbnailUrl: "" }
                              : r
                          )
                        );
                        setThumbnailProgressMessages((prev) => ({
                          ...prev,
                          [request.songRequestId]: "",
                        }));
                      }}
                      style={{ marginLeft: "10px" }}
                    >
                      삭제
                    </button>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <button
                      onClick={(e) => {
                        console.log(e);
                        handleThumbnailClick(request);
                      }}
                    >
                      썸네일 선택
                    </button>
                    <button
                      onClick={() => {
                        const fileInput = document.getElementById(
                          `file-input-${request.songRequestId}`
                        );
                        fileInput?.click();
                      }}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      직접 업로드
                    </button>
                    <input
                      id={`file-input-${request.songRequestId}`}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }} // input을 숨김
                      onChange={(e) =>
                        handleThumbnailChange(e, request.songRequestId)
                      }
                    />
                    <div>
                      {thumbnailProgressMessages[request.songRequestId]}
                    </div>
                  </div>
                )}
              </td>
              <td>{request.userName}</td>
              <td>{request.status}</td>
              <td>
                <button onClick={() => handleComplete(request)}>완료</button>
              </td>
              <td>
                <button onClick={() => handleFail(request)}>실패</button>
              </td>
              <td>
                <button
                  onClick={() => {
                    let songId;
                    do {
                      songId = prompt("곡 ID를 입력하세요 (00000 ~ 99999):");
                      if (songId === null) {
                        // 사용자가 취소를 누른 경우 함수 종료
                        return;
                      }
                      // 곡 ID가 5자리 숫자인지 확인
                      if (!/^\d{5}$/.test(songId)) {
                        alert(
                          "곡 ID는 00000부터 99999까지의 5자리 숫자여야 합니다. 다시 입력해주세요."
                        );
                        songId = null; // 유효하지 않으면 다시 입력 받도록 설정
                      }
                    } while (!songId);

                    handleCompleteNoMaker(request, songId);
                  }}
                >
                  완료 (메이커x)
                </button>
              </td>
              <td>
                <button onClick={() => handleDelete(request)}>삭제</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {isModalOpen && modalArtistName && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>썸네일 선택 ({modalArtistName})</h2>
            <div className="thumbnail-selection-modal">
              {totalArtistThumbnailList[modalArtistName]?.map(
                (thumbnailUrl) => (
                  <img
                    key={thumbnailUrl}
                    src={thumbnailUrl}
                    alt="썸네일"
                    style={{ objectFit: "cover", width: 192, height: 100 }}
                    onClick={() =>
                      handleThumbnailSelect(thumbnailUrl, editingId as string)
                    }
                  />
                )
              )}
            </div>
            <button onClick={() => setIsModalOpen(false)}>닫기</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MakingRequestPage;
